import useRemoveEnergyPerformanceCertificate from '@/api/mutations/energyPerformanceCertificate/useRemoveEnergyPerformanceCertificate';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { ProjectByIdQuery } from '@/gql/graphql';
import { SyncEnergyPerformanceCertificateButton } from '@/ui/pages/projectPage/components/PropertyPanel/SyncEnergyPerformanceCertificateButton';
import {
  Accordion,
  Button,
  Card,
  Divider,
  ManageableItemsList,
  Modal,
} from '@payaca/ambrosia';

import { EpcRatingBadge } from '@payaca/components/epcRatingBadge';
import {
  DateFormats,
  getInternationalDateFormatByLocale,
} from '@payaca/helpers/internationalHelper';
import UntitledIcon from '@payaca/untitled-icons';
import { formatDate } from 'date-fns';
import { FC, useState } from 'react';
import { GridElement } from './GridElement';

type EnergyPerformanceCertificateCardProps = {
  projectId: string;
  siteAddressPublicId: string;
  energyPerformanceCertificate: ProjectByIdQuery['projectById']['addresses']['site'][number]['energyPerformanceCertificates'][number];
};

const EnergyPerformanceCertificateCard: FC<
  EnergyPerformanceCertificateCardProps
> = (props) => {
  const {
    projectId,
    siteAddressPublicId,
    energyPerformanceCertificate: epc,
  } = props;

  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] =
    useState(false);

  const { mutate: removeEpc, isPending: isRemovingEpc } =
    useRemoveEnergyPerformanceCertificate({ projectId });

  const handleRemove = () => {
    removeEpc(
      {
        addressId: siteAddressPublicId,
      },
      {
        onSuccess: () => {
          setIsConfirmRemoveModalOpen(false);
        },
      }
    );
  };

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div className="flex justify-between">
          <h3>Energy Performance Certificate (EPC)</h3>

          <div className="space-x-2">
            <SyncEnergyPerformanceCertificateButton
              projectId={projectId}
              addressId={siteAddressPublicId}
              variant="white"
              size="sm"
            >
              Refresh <UntitledIcon name="refresh-cw-01" className="w-4 h-4" />
            </SyncEnergyPerformanceCertificateButton>
            <Button
              variant="white"
              size="sm"
              onClick={() => setIsConfirmRemoveModalOpen(true)}
            >
              Remove <UntitledIcon name="trash-01.3" className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex">
          {/* Ratings Section */}
          <div className="space-y-4 border-r-1 pr-6">
            {/* Current Rating */}
            {epc.energyPerformance.currentEnergyRating && (
              <GridElement title="Current Rating">
                <EpcRatingBadge
                  rating={epc.energyPerformance.currentEnergyRating}
                  energyEfficiency={
                    epc.energyPerformance.currentEnergyEfficiency
                  }
                />
              </GridElement>
            )}

            {/* Potential Rating */}
            {epc.energyPerformance.potentialEnergyRating && (
              <GridElement title="Potential Rating">
                <EpcRatingBadge
                  rating={epc.energyPerformance.potentialEnergyRating}
                  energyEfficiency={
                    epc.energyPerformance.potentialEnergyEfficiency
                  }
                />
              </GridElement>
            )}
          </div>

          <div className="pl-6 grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 flex-1">
            <GridElement title="Lodged">
              <FormattedDate
                date={epc.metadata.lodgementDate}
                format={DateFormats.MID}
              />
            </GridElement>
            <GridElement title="Inspected">
              <FormattedDate
                date={epc.metadata.inspectionDate}
                format={DateFormats.MID}
              />
            </GridElement>
            <GridElement title="Property Type">
              {epc.property.propertyType}
            </GridElement>
            <GridElement title="Built Form">
              {epc.property.builtForm}
            </GridElement>
            <GridElement title="Construction Age">
              {epc.property.constructionAgeBand}
            </GridElement>
            <GridElement title="Tenure">
              <p className="capitalize">{epc.property.tenure}</p>
            </GridElement>
            <GridElement title="Total Floor Area">
              {epc.property.totalFloorArea} m²
            </GridElement>
            <GridElement title="Habitable Rooms">
              {epc.property.numberHabitableRooms}
            </GridElement>
            <GridElement title="Certificate Number">
              {epc.metadata.certificateNumber}
            </GridElement>
            <GridElement title="Environmental Impact">
              {epc.energyPerformance.currentEnvironmentImpact} (Current) /{' '}
              {epc.energyPerformance.potentialEnvironmentImpact} (Potential)
            </GridElement>
          </div>
        </div>

        <Divider />

        <Accordion title="Features in this property" panelClassName="space-y-4">
          <ManageableItemsList>
            <ManageableItemsList.Table
              uniqueKey="id"
              items={[
                {
                  id: 'walls',
                  title: 'Walls',
                  description: epc.buildingComponents.walls.description,
                  rating: epc.buildingComponents.walls.energyEfficiency,
                },
                {
                  id: 'roof',
                  title: 'Roof',
                  description: epc.buildingComponents.roof.description,
                  rating: epc.buildingComponents.roof.energyEfficiency,
                },
                {
                  id: 'floor',
                  title: 'Floor',
                  description: epc.buildingComponents.floor.description,
                  rating: epc.buildingComponents.floor.energyEfficiency,
                },
                {
                  id: 'windows',
                  title: 'Windows',
                  description: epc.buildingComponents.windows.description,
                  rating: epc.buildingComponents.windows.energyEfficiency,
                },
                {
                  id: 'main-heating',
                  title: 'Main Heating',
                  description: epc.buildingComponents.mainHeating.description,
                  rating: epc.buildingComponents.mainHeating.energyEfficiency,
                },
                {
                  id: 'main-heating-controls',
                  title: 'Main Heating Controls',
                  description:
                    epc.buildingComponents.mainHeatingControls.description,
                  rating:
                    epc.buildingComponents.mainHeatingControls.energyEfficiency,
                },
                {
                  id: 'secondary-heating',
                  title: 'Secondary Heating',
                  description:
                    epc.buildingComponents.secondaryHeating.description,
                  rating:
                    epc.buildingComponents.secondaryHeating.energyEfficiency,
                },
                {
                  id: 'hot-water',
                  title: 'Hot Water',
                  description: epc.buildingComponents.hotWater.description,
                  rating: epc.buildingComponents.hotWater.energyEfficiency,
                },
                {
                  id: 'lighting',
                  title: 'Lighting',
                  description: epc.buildingComponents.lighting.description,
                  rating: epc.buildingComponents.lighting.energyEfficiency,
                },
              ]}
            >
              <ManageableItemsList.Table.Column
                header="Feature"
                field="title"
                className="align-top"
              />
              <ManageableItemsList.Table.Column
                header="Description"
                field="description"
                className="whitespace-normal align-top"
              />
              <ManageableItemsList.Table.Column
                header="Rating"
                field="rating"
                className="align-top"
              />
            </ManageableItemsList.Table>
          </ManageableItemsList>
        </Accordion>

        <Divider />

        <Accordion
          title="Additional Property Details"
          panelClassName="space-y-4"
        >
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Unique Property Reference Number (UPRN)">
              {epc.property.uprn}
            </GridElement>
            <GridElement title="UPRN Source">
              {epc.property.uprnSource}
            </GridElement>
            <GridElement title="Local Authority">
              <p>{epc.property.localAuthority}</p>
              <p>{epc.property.localAuthorityLabel}</p>
            </GridElement>
            <GridElement title="Constituency">
              <p>{epc.property.constituency}</p>
              <p>{epc.property.constituencyLabel}</p>
            </GridElement>
            <GridElement title="Number Heated Rooms">
              {epc.property.numberHeatedRooms}
            </GridElement>
            <GridElement title="Number Open Fireplaces">
              {epc.property.numberOpenFireplaces}
            </GridElement>
            <GridElement title="Flat Top Storey">
              {epc.property.flatTopStorey === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Flat Storey Count">
              {epc.property.flatStoreyCount}
            </GridElement>
            <GridElement title="Floor Level">
              {epc.property.floorLevel}
            </GridElement>
            <GridElement title="Extension Count">
              {epc.property.extensionCount}
            </GridElement>
            <GridElement title="Transaction Type">
              <p className="capitalize">{epc.property.transactionType}</p>
            </GridElement>
          </div>

          <Divider />

          <h4>Energy Performance Details</h4>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Current Energy Consumption">
              {epc.energyPerformance.currentEnergyConsumption} kWh/m² per year
            </GridElement>
            <GridElement title="Potential Energy Consumption">
              {epc.energyPerformance.potentialEnergyConsumption} kWh/m² per year
            </GridElement>
            <GridElement title="Current CO2 Emissions">
              {epc.energyPerformance.currentCo2Emissions} tonnes per year
            </GridElement>
            <GridElement title="Potential CO2 Emissions">
              {epc.energyPerformance.potentialCo2Emissions} tonnes per year
            </GridElement>
            <GridElement title="Current CO2 Emissions Per Floor Area">
              {epc.energyPerformance.currentCo2EmissionsPerFloorArea} kg/m²
            </GridElement>
          </div>

          <Divider />

          <h4>Energy Costs</h4>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Current Heating Cost">
              £{epc.costs.currentHeatingCost}
            </GridElement>
            <GridElement title="Potential Heating Cost">
              £{epc.costs.potentialHeatingCost}
            </GridElement>
            <GridElement title="Current Hot Water Cost">
              £{epc.costs.currentHotWaterCost}
            </GridElement>
            <GridElement title="Potential Hot Water Cost">
              £{epc.costs.potentialHotWaterCost}
            </GridElement>
            <GridElement title="Current Lighting Cost">
              £{epc.costs.currentLightingCost}
            </GridElement>
            <GridElement title="Potential Lighting Cost">
              £{epc.costs.potentialLightingCost}
            </GridElement>
          </div>

          <Divider />

          <h4>Technical Specifications</h4>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4 mt-4">
            <GridElement title="Main Fuel">
              <p className="capitalize">{epc.technical.mainFuel}</p>
            </GridElement>
            <GridElement title="Main Heating Controls">
              {epc.technical.mainHeatingControls}
            </GridElement>
            <GridElement title="Floor Height">
              {epc.technical.floorHeight} m
            </GridElement>
            <GridElement title="Glazed Type">
              <p className="capitalize">{epc.technical.glazedType}</p>
            </GridElement>
            <GridElement title="Glazed Area">
              {epc.technical.glazedArea}
            </GridElement>
            <GridElement title="Multi Glaze Proportion">
              {epc.technical.multiGlazeProportion}%
            </GridElement>
            <GridElement title="Low Energy Lighting">
              {epc.technical.lowEnergyLighting}%
            </GridElement>
            <GridElement title="Low Energy Fixed Light Count">
              {epc.technical.lowEnergyFixedLightCount}
            </GridElement>
            <GridElement title="Fixed Lighting Outlets Count">
              {epc.technical.fixedLightingOutletsCount}
            </GridElement>
            <GridElement title="Energy Tariff">
              {epc.technical.energyTariff}
            </GridElement>
            <GridElement title="Mains Gas Available">
              {epc.technical.mainsGasFlag === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Solar Water Heating">
              {epc.technical.solarWaterHeatingFlag === 'Y' ? 'Yes' : 'No'}
            </GridElement>
            <GridElement title="Mechanical Ventilation">
              <p className="capitalize">
                {epc.technical.mechanicalVentilation}
              </p>
            </GridElement>
            <GridElement title="Photo Supply">
              {epc.technical.photoSupply} kW
            </GridElement>
            <GridElement title="Wind Turbine Count">
              {epc.technical.windTurbineCount}
            </GridElement>
            <GridElement title="Heat Loss Corridor">
              <p className="capitalize">{epc.technical.heatLossCorridor}</p>
            </GridElement>
            <GridElement title="Unheated Corridor Length">
              {epc.technical.unheatedCorridorLength} m²
            </GridElement>
          </div>
        </Accordion>
      </Card.Body>

      <Modal
        isOpen={isConfirmRemoveModalOpen}
        onClose={() => setIsConfirmRemoveModalOpen(false)}
        title="Remove EPC?"
      >
        <Modal.Body>
          <p>
            Are you sure you want to remove this Energy Performance Certificate?
            You can search for it again later if needed.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="white"
              onClick={() => setIsConfirmRemoveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              colour="red"
              onClick={handleRemove}
              isProcessing={isRemovingEpc}
            >
              Remove
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

// TODO put this into shared components.
const FormattedDate: FC<{
  date: Date | string | null | undefined;
  format?: DateFormats;
}> = (props) => {
  const { date, format = DateFormats.SHORT } = props;

  const { data: accountRegionPreferences } = useGetMyRegionPreferences();

  if (!date) return null;

  return formatDate(
    date,
    getInternationalDateFormatByLocale(format, accountRegionPreferences?.locale)
  );
};

export default EnergyPerformanceCertificateCard;
